import { FC, useEffect, useMemo, useState } from 'react';

import { useRecoilState } from 'recoil';

import { BugoData } from '@shared/api/bugo/bugo.interface';
import { CourtesyContent } from '@shared/api/courtesyAsset/courtesyAsset.interface';
import Tabs from '@shared/components/Tabs';
import Select from '@shared/containers/Select/Select';
import { useForm, useWatch } from 'react-hook-form';

import {
  courtesyContentAtom,
  courtesyContentStringAtom,
  useDeceasedNameAtom,
} from './courtesy.atom';
import { getCourtesyStringFromContent } from './courtesyGenerator';

interface Props {
  courtesyContentList: CourtesyContent[];
  bugo: BugoData;
  // 기간이 지난 부고: 고인명·상주명·관계를 노출하지 않는다.
  anonymous?: boolean;
}

//* 해당 멤버가 답례글에서 예문 선택기
export const MemberCourtesyContentSelector: FC<Props> =
  function MemberCourtesyContentSelector({
    courtesyContentList,
    bugo,
    anonymous = false,
  }: Props) {
    const [courtesyContent, setCourtesyContent] = useRecoilState(courtesyContentAtom);

    const [courtesyContentString, setCourtesyContentString] = useRecoilState(
      courtesyContentStringAtom,
    );

    const [useDeceasedName, setUseDeceasedName] = useRecoilState(useDeceasedNameAtom);
    //!reverseType (익명 모드에선 관계도 감추므로 고인명 표시 토글 자체를 없앤다)
    const reverseType = anonymous ? undefined : bugo?.member?.reverseType;
    const hasReverseType = !!reverseType;

    const deafaultCourtesyContentId = bugo?.fevent?.courtesyContent;

    const defaultCourtesyContent = courtesyContentList.find((item) => {
      return item._id === deafaultCourtesyContentId;
    });

    // !초기값 courtesyContent  -> defaultCourtesyContent(fevent) -> 0번째
    const { control, setValue } = useForm({
      defaultValues: {
        courtesyContent:
          courtesyContent?.name ??
          defaultCourtesyContent?.name ??
          courtesyContentList[0]?.name,
        courtesyContentString: courtesyContent
          ? getCourtesyStringFromContent(courtesyContent, bugo, useDeceasedName, anonymous)
          : defaultCourtesyContent
          ? getCourtesyStringFromContent(
              defaultCourtesyContent,
              bugo,
              useDeceasedName,
              anonymous,
            )
          : courtesyContentList[0]
          ? getCourtesyStringFromContent(
              courtesyContentList[0],
              bugo,
              useDeceasedName,
              anonymous,
            )
          : '',
      },
    });

    const courtesyContentSetList = useMemo(() => {
      const courtesyContentCategory = courtesyContentList.map((item) => {
        return item.name.split('(')[0];
      });
      const categorySet = Array.from(new Set(courtesyContentCategory)) as string[];
      return categorySet;
    }, [courtesyContentList]);

    // !tab 초기값  default(fevent)
    const [selectedTab, setSelectedTab] = useState<number>(
      // index === -1이면 0 처리
      Math.max(
        courtesyContentSetList.indexOf(defaultCourtesyContent?.name?.split('(')[0] ?? ''),
        0,
      ),
    );

    // 해당 탭 클릭시 해당 탭에서 선택 가능한 예문 리스트
    const categoriedCourtesyContentList = courtesyContentList.filter(
      (courtesyContent) => {
        return courtesyContent.name.split('(')[0] === courtesyContentSetList[selectedTab];
      },
    );

    // 해당 탭 클릭시 첫번째걸로 courtesyContent 변경
    const onTabClick = (tab: number) => {
      const tabCourtesyCotentList = courtesyContentList.filter((courtesyContent) => {
        return courtesyContent.name.split('(')[0] === courtesyContentSetList[tab];
      });
      setValue('courtesyContent', tabCourtesyCotentList[0]?.name);
    };

    const courtesyContentName = useWatch({ control, name: 'courtesyContent' });

    //! control courtesyContent 변경시 recoilState CourtesyContent courtesyContentString 변경되도록함

    useEffect(() => {
      const courtesyContentByName = courtesyContentList.find((item) => {
        return item.name === courtesyContentName;
      });
      if (courtesyContentByName && courtesyContent?.name !== courtesyContentByName.name) {
        setCourtesyContent(courtesyContentByName);

        setCourtesyContentString(
          getCourtesyStringFromContent(
            courtesyContentByName,
            bugo,
            useDeceasedName,
            anonymous,
          ),
        );
      }
    }, [
      anonymous,
      bugo,
      courtesyContent,
      courtesyContentList,
      courtesyContentName,
      setCourtesyContent,
      setCourtesyContentString,
      useDeceasedName,
    ]);

    useEffect(() => {
      if (courtesyContent) {
        setCourtesyContentString(
          getCourtesyStringFromContent(courtesyContent, bugo, useDeceasedName, anonymous),
        );
      }
    }, [anonymous, bugo, courtesyContent, setCourtesyContentString, useDeceasedName]);

    return (
      <>
        <div className="bg-white pt-4">
          <h1 className="bugo-h1 px-4">답례글 선택</h1>
          <Tabs
            items={courtesyContentSetList}
            selectedTab={selectedTab}
            setSelectedTab={setSelectedTab}
            onTabClick={onTabClick}
          />
          {/* 예문 상세 선택 */}
        </div>
        <div className="flex items-center gap-2 px-4 text-sm font-medium">
          <div className={hasReverseType ? 'w-2/3' : 'w-full'}>
            <Select
              name="courtesyContent"
              placeholder={'예문 선택'}
              control={control}
              optionList={categoriedCourtesyContentList.map((item) => {
                return item.name;
              })}
              defaultValue={categoriedCourtesyContentList[0]?.name}
            ></Select>
          </div>
          {hasReverseType && (
            <label className="flex w-1/3 cursor-pointer items-center justify-end gap-2">
              <span className="text-xs text-gray-600">고인명 표시</span>
              <button
                type="button"
                role="switch"
                aria-checked={useDeceasedName}
                onClick={() => setUseDeceasedName((prev) => !prev)}
                className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${
                  useDeceasedName ? 'theme-bg-main' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${
                    useDeceasedName ? 'translate-x-[18px]' : 'translate-x-[2px]'
                  }`}
                />
              </button>
            </label>
          )}
        </div>
        <div className="relative px-4 text-sm">
          <div className="pointer-events-none absolute right-6 top-2 flex items-center gap-1 text-xs font-medium text-red-400">
            <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
              <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
            </svg>
            <span>클릭시 편집 가능</span>
          </div>
          <textarea
            className="min-h-[328px] w-full border bg-white px-4 py-3 pt-6 text-sm text-[12px] theme-border-main"
            value={courtesyContentString ?? ''}
            onChange={(e) => {
              setCourtesyContentString(e.target.value);
            }}
          ></textarea>
        </div>
      </>
    );
  };
